import type { SurveyAnswers } from "@bezoned/shared/schemas/survey/survey.schema.js";
import { useEffect, useRef, useState } from "react";

import {
  appSurveyActiveGet,
  appSurveyResponseCreate,
} from "../../services/api.service.js";
import { useLayoutStore } from "../../store/layout.store.js";
import { recordErrorInSentry } from "../../utils/init-sentry.js";

type ActiveSurveyState = Awaited<ReturnType<typeof appSurveyActiveGet>>;

interface SurveyRendererProps {
  msTeamId: string;
}

function reportSurveyActionError(error: unknown): void {
  recordErrorInSentry(
    error instanceof Error ? error : new Error(String(error))
  );
}

export const SurveyRenderer = ({ msTeamId }: SurveyRendererProps) => {
  const actionInFlightRef = useRef(false);
  const attemptedTeamIdRef = useRef<string | null>(null);
  const [activeSurveyState, setActiveSurveyState] =
    useState<ActiveSurveyState | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const modalVariant = useLayoutStore((state) => state.modalVariant);
  const hideSurveyModal = useLayoutStore((state) => state.hideSurveyModal);
  const setModalVariant = useLayoutStore((state) => state.setModalVariant);

  useEffect(() => {
    if (attemptedTeamIdRef.current === msTeamId) {
      return;
    }

    attemptedTeamIdRef.current = msTeamId;
    actionInFlightRef.current = false;
    setActiveSurveyState(null);
    setSubmitting(false);
    hideSurveyModal();

    let cancelled = false;

    const fetchActiveSurvey = async () => {
      try {
        const result = await appSurveyActiveGet();

        if (cancelled || !result.survey) {
          return;
        }

        setActiveSurveyState(result);
      } catch (error) {
        recordErrorInSentry(error);
      }
    };

    fetchActiveSurvey().catch(recordErrorInSentry);

    return () => {
      cancelled = true;
      hideSurveyModal();
    };
  }, [hideSurveyModal, msTeamId]);

  useEffect(() => {
    if (
      activeSurveyState?.survey &&
      modalVariant !== null &&
      modalVariant !== "survey"
    ) {
      setActiveSurveyState(null);
      setSubmitting(false);
    }
  }, [activeSurveyState, modalVariant]);

  useEffect(() => {
    const survey = activeSurveyState?.survey;
    if (!survey) {
      return;
    }

    const closeSurvey = () => {
      setActiveSurveyState(null);
      setSubmitting(false);
      hideSurveyModal();
    };

    const persistSurveyAction = async (
      action:
        | { action: "filled"; answers: SurveyAnswers }
        | { action: "skipped" }
        | { action: "snoozed" },
      closeModal = true
    ) => {
      if (actionInFlightRef.current) {
        return;
      }

      actionInFlightRef.current = true;
      if (closeModal) {
        closeSurvey();
      }

      try {
        await appSurveyResponseCreate({
          surveyId: survey.id,
          ...action,
        });
      } catch (error) {
        reportSurveyActionError(error);
      }
    };

    setModalVariant("survey", {
      onRatingClose: closeSurvey,
      onSkip: () => {
        persistSurveyAction({ action: "skipped" }).catch(recordErrorInSentry);
      },
      onSnooze: () => {
        persistSurveyAction({ action: "snoozed" }).catch(recordErrorInSentry);
      },
      onSubmit: (answers: SurveyAnswers) => {
        persistSurveyAction(
          { action: "filled", answers },
          !survey.behavior.askForRating
        ).catch(reportSurveyActionError);
      },
      showSnoozeButton: activeSurveyState.showSnoozeButton,
      submitting,
      survey,
    });
  }, [activeSurveyState, hideSurveyModal, setModalVariant, submitting]);

  return null;
};
